// Persona-supplied variables are ordinary top-level PersonaSpec keys. The
// declarations determine which keys are offered; no scenario-to-persona link
// is stored or inferred (docs/01-product-model.md (The variable system (the
// cascade))).
import { useState } from "react";
import { docUrl } from "../../lib/docLinks";
import { requestErrorDescription } from "../../lib/errors";
import { useVariableDeclarations } from "../../lib/useVariableDeclarations";
import {
  cascadeFlags,
  declarationConflict,
  declarationConflictMessage,
  groupDeclarationsByKey,
  isPersonaSuppliableDeclaration,
  type VariableResponse,
} from "../../lib/variableCascade";
import { unicodeCaseFold } from "../../lib/unicodeCaseFold";
import {
  Button,
  HelpTip,
  Inline,
  InlineError,
  RouterLink,
  Skeleton,
  Stack,
  Text,
} from "../../ui";
import { VariableValueField } from "../variables/VariableValueField";
import type { PersonaVariableValues } from "./personaSpec";

type PersonaSpecValues = Record<string, unknown>;

interface PersonaVariablesSectionProps {
  agentId: string;
  declarations?: readonly VariableResponse[];
  isPending: boolean;
  personaId?: string;
  spec: PersonaSpecValues | null;
  values: PersonaVariableValues;
  onValueChange: (key: string, value: unknown) => void;
}

export function PersonaVariablesSection({
  declarations,
  ...props
}: PersonaVariablesSectionProps) {
  return (
    <Stack gap={3}>
      <Inline align="center" gap={2} justify="between">
        <Inline align="center" gap={2}>
          <Text variant="caps">Variables</Text>
          <HelpTip
            label="About Persona variables"
            docHref={docUrl("guides/use-variables-in-simulations")}
            docLabel="Use variables in simulations"
          >
            {
              "Supply caller details for this Persona. Fields come from this Persona's declarations and Scenario inputs without defaults. Values match by name; a Scenario default overrides a Persona value. Create declarations in a new tab, then return here to fill them."
            }
          </HelpTip>
        </Inline>
        {props.agentId.length > 0 ? (
          <RouterLink
            params={{ agentId: props.agentId }}
            search={{
              new: 1,
              ...(props.personaId === undefined
                ? {}
                : { scope_type: "persona", scope_id: props.personaId }),
            }}
            target="_blank"
            rel="noopener noreferrer"
            to="/agents/$agentId/variables"
          >
            Declare variable
          </RouterLink>
        ) : null}
      </Inline>
      {props.agentId.length > 0 && props.personaId === undefined ? (
        <Text variant="muted">
          Save this Persona before creating its own variables.
        </Text>
      ) : null}
      {props.agentId.length === 0 ? (
        <Text variant="muted">Choose an Agent to load variables.</Text>
      ) : declarations !== undefined ? (
        <PersonaVariablesSectionView declarations={declarations} {...props} />
      ) : (
        <PersonaVariablesQuery {...props} />
      )}
    </Stack>
  );
}

function PersonaVariablesQuery({
  agentId,
  isPending,
  personaId,
  spec,
  values,
  onValueChange,
}: PersonaVariablesSectionProps) {
  const declarations = useVariableDeclarations(agentId);
  if (declarations.isPending) {
    return <Skeleton label="Loading variables" rows={2} shape="detail-field" />;
  }
  return (
    <Stack gap={3}>
      {declarations.isError ? (
        <Inline gap={2}>
          <InlineError>
            {requestErrorDescription(
              declarations.error,
              "Variables failed to load.",
            )}
          </InlineError>
          <Button
            size="sm"
            type="button"
            variant="secondary"
            onClick={() => void declarations.refetch()}
          >
            Retry variables
          </Button>
        </Inline>
      ) : null}
      {declarations.data === undefined ? null : (
        <PersonaVariablesSectionView
          declarations={declarations.data}
          isPending={isPending}
          personaId={personaId}
          spec={spec}
          values={values}
          onValueChange={onValueChange}
        />
      )}
    </Stack>
  );
}

export interface PersonaVariablesSectionViewProps {
  declarations: readonly VariableResponse[];
  isPending: boolean;
  personaId?: string;
  spec: PersonaSpecValues | null;
  values: PersonaVariableValues;
  onValueChange: (key: string, value: unknown) => void;
}

export function PersonaVariablesSectionView({
  declarations,
  isPending,
  personaId,
  spec,
  values,
  onValueChange,
}: PersonaVariablesSectionViewProps) {
  const offered = personaVariableDeclarationGroups(declarations, personaId);
  if (offered.size === 0) {
    return <Text variant="muted">No variables</Text>;
  }
  return (
    <Stack gap={3}>
      {[...offered.values()].map((group) => {
        const declaration = group[0];
        if (!declaration) return null;
        const flags = cascadeFlags(group);
        const conflict = declarationConflict(group);
        const isRedactedSensitiveEnum =
          flags.isSensitive &&
          declaration.current_revision.value_type === "enum" &&
          declaration.current_revision.allowed_values?.length === 1 &&
          declaration.current_revision.allowed_values[0] === "[redacted]";
        const value = personaVariableValue(declaration.key, spec, values);
        const input = variableInput(
          declaration,
          value,
          isPending || conflict !== "none",
          flags.isSensitive,
          (next) => {
            onValueChange(declaration.key, next);
          },
        );
        return (
          <Stack key={unicodeCaseFold(declaration.key)} gap={1}>
            {flags.isSensitive && !isRedactedSensitiveEnum ? (
              <Text as="span" variant="masked">
                {input}
              </Text>
            ) : (
              input
            )}
            {flags.isRequired && value === undefined ? (
              <Text variant="muted">Required — no value</Text>
            ) : null}
            {conflict !== "none" ? (
              <InlineError>
                {declarationConflictMessage(
                  declaration.key,
                  group.length,
                  conflict,
                )}
              </InlineError>
            ) : null}
          </Stack>
        );
      })}
    </Stack>
  );
}

// Scenario declarations without defaults are persona contracts. An existing
// persona may also edit its own persona-scope declaration; create has no id.
export function personaVariableDeclarationGroups(
  declarations: readonly VariableResponse[],
  personaId?: string,
): Map<string, VariableResponse[]> {
  const offered = declarations.filter((declaration) =>
    isPersonaSuppliableDeclaration(
      {
        hasDefault: declaration.current_revision.default_value !== null,
        scopeId: declaration.scope_id,
        scopeType: declaration.scope_type,
      },
      personaId,
    ),
  );
  return groupDeclarationsByKey([...offered]);
}

export function personaVariableDeclarations(
  declarations: readonly VariableResponse[],
  personaId?: string,
): VariableResponse[] {
  return [...personaVariableDeclarationGroups(declarations, personaId).values()]
    .filter((group) => declarationConflict(group) === "none")
    .map((group) => group[0])
    .filter(
      (declaration): declaration is VariableResponse =>
        declaration !== undefined,
    );
}

export function personaSupplyCount(
  declarations: readonly VariableResponse[],
  personaId: string,
  spec: PersonaSpecValues | null,
): number {
  return personaVariableDeclarations(declarations, personaId).filter(
    (declaration) => personaSpecValue(spec, declaration.key) !== undefined,
  ).length;
}

function personaVariableValue(
  key: string,
  spec: PersonaSpecValues | null,
  values: PersonaVariableValues,
): unknown {
  const draftKey = Object.keys(values).find(
    (candidate) => unicodeCaseFold(candidate) === unicodeCaseFold(key),
  );
  return draftKey === undefined
    ? personaSpecValue(spec, key)
    : values[draftKey];
}

function personaSpecValue(
  spec: PersonaSpecValues | null,
  key: string,
): unknown {
  const matchingKey =
    spec === null
      ? undefined
      : Object.keys(spec).find(
          (candidate) => unicodeCaseFold(candidate) === unicodeCaseFold(key),
        );
  if (spec === null || matchingKey === undefined) {
    return undefined;
  }
  const value = spec[matchingKey];
  return isJsonScalar(value) ? value : undefined;
}

function isJsonScalar(
  value: unknown,
): value is string | number | boolean | null {
  return (
    value === null ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  );
}

function variableInput(
  declaration: VariableResponse,
  value: unknown,
  disabled: boolean,
  isSensitive: boolean,
  onChange: (value: unknown) => void,
) {
  const valueType = declaration.current_revision.value_type;
  const text = value === undefined || value === null ? "" : scalarText(value);
  // allowed_values is JsonValue[]: a declaration may offer numbers or
  // booleans, so every entry is displayed through the shared scalar text and
  // mapped back to its own JSON value on selection.
  const allowed = declaration.current_revision.allowed_values ?? [];
  const allowedTexts = allowed.map((candidate) => scalarText(candidate));
  if (
    isSensitive &&
    valueType === "enum" &&
    allowedTexts.length === 1 &&
    allowedTexts[0] === "[redacted]"
  ) {
    return <Text variant="muted">Value unavailable.</Text>;
  }
  if (valueType === "bool" || valueType === "enum") {
    return (
      <VariableValueField
        allowedValues={allowedTexts}
        disabled={disabled}
        emptyLabel="No value"
        label={declaration.key}
        value={text}
        valueType={valueType}
        onChange={(next) => {
          if (next.length === 0) {
            onChange(undefined);
            return;
          }
          if (valueType === "bool") {
            onChange(next === "true");
            return;
          }
          onChange(
            allowed.find((candidate) => scalarText(candidate) === next) ?? next,
          );
        }}
      />
    );
  }
  return (
    <PersonaScalarInput
      disabled={disabled}
      label={declaration.key}
      value={text}
      valueType={valueType}
      onChange={onChange}
    />
  );
}

// A text-typed persona value needs a raw buffer: the parent stores the parsed
// JSON value, so an in-progress "1." or "-" would otherwise be erased on the
// round trip. The buffer resyncs when the stored value changes underneath it.
function PersonaScalarInput({
  disabled,
  label,
  onChange,
  value,
  valueType,
}: {
  disabled: boolean;
  label: string;
  onChange: (value: unknown) => void;
  value: string;
  valueType: string;
}) {
  const [text, setText] = useState(value);
  const [syncedValue, setSyncedValue] = useState(value);
  const [error, setError] = useState<string | null>(null);
  if (value !== syncedValue) {
    setSyncedValue(value);
    setText(value);
    setError(null);
  }
  return (
    <Stack gap={1}>
      <VariableValueField
        allowedValues={[]}
        disabled={disabled}
        label={label}
        value={text}
        valueType={valueType}
        onChange={(next) => {
          setText(next);
          if (next.length === 0) {
            setError(null);
            onChange(undefined);
            return;
          }
          if (valueType !== "number") {
            setError(null);
            onChange(next);
            return;
          }
          const parsed = Number(next);
          if (!Number.isFinite(parsed)) {
            setError("Enter a finite number.");
            return;
          }
          if (Number.isInteger(parsed) && !Number.isSafeInteger(parsed)) {
            setError("Enter a safe integer.");
            return;
          }
          setError(null);
          onChange(parsed);
        }}
      />
      {error === null ? null : <InlineError>{error}</InlineError>}
    </Stack>
  );
}

function scalarText(value: unknown): string {
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }
  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }
  return "";
}
